import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { adminApi } from '../services/api.ts';
import DocumentoDetalleCompleto from '../components/DocumentoDetalleCompleto.tsx';

interface Documento {
  id: string;
  titulo: string;
  descripcion?: string;
  categoria: string;
  estado: string;
  fechaCreacion: string;
  fechaExpiracion: string;
  firmante: string;
  email: string;
  firmantes?: Array<{
    id: string;
    nombre: string;
    email: string;
    telefono: string;
    tipoDocumento: string;
    numeroDocumento: string;
    esEmpresa: boolean;
    estado: string;
  }>;
  totalFirmantes?: number;
}

const DocumentosLista: React.FC = () => {
  const navigate = useNavigate();
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocumentoId, setSelectedDocumentoId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroActivo, setFiltroActivo] = useState<string>('TODOS');

  useEffect(() => {
    loadDocumentos();
  }, []);

  const loadDocumentos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getDocumentos();
      setDocumentos(response.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando documentos');
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'FIRMADO':
      case 'COMPLETADO':
        return 'success';
      case 'FIRMANDO':
        return 'warning';
      case 'PENDIENTE':
        return 'info';
      case 'CANCELADO':
        return 'error';
      default:
        return 'default';
    }
  };

  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'FIRMADO':
      case 'COMPLETADO':
        return <CheckCircleIcon />;
      case 'CANCELADO':
        return <ErrorIcon />;
      default:
        return <WarningIcon />;
    }
  };

  const handleFiltroClick = (estado: string) => {
    setFiltroActivo(estado);
  };

  const documentosFiltrados = documentos.filter(doc => {
    if (filtroActivo === 'TODOS') return true;
    return doc.estado === filtroActivo;
  });


  const handleVerDetalle = (documentoId: number) => {
    setSelectedDocumentoId(documentoId);
  };

  const handleCloseDetalle = () => {
    setSelectedDocumentoId(null);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No especificada';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredDocumentos = documentosFiltrados.filter(documento =>
    documento.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    documento.firmante.toLowerCase().includes(searchTerm.toLowerCase()) ||
    documento.estado.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calcular contadores por estado
  const getEstadoCounts = () => {
    const counts = {
      PENDIENTE: 0,
      FIRMANDO: 0,
      COMPLETADO: 0,
      EXPIRADO: 0,
      CANCELADO: 0
    };

    documentos.forEach(documento => {
      if (counts.hasOwnProperty(documento.estado)) {
        counts[documento.estado as keyof typeof counts]++;
      }
    });

    return counts;
  };

  const estadoCounts = getEstadoCounts();

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Cargando documentos...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadDocumentos}>
            Reintentar
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          Documentos
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            color="primary"
            onClick={() => navigate('/admin')}
          >
            Nuevo Documento
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadDocumentos}
            variant="outlined"
          >
            Actualizar
          </Button>
        </Box>
      </Box>

      {/* Filtros por estado */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Chip
          icon={<CheckCircleIcon />}
          label={`Todos: ${documentos.length}`}
          color={filtroActivo === 'TODOS' ? 'primary' : 'default'}
          variant={filtroActivo === 'TODOS' ? 'filled' : 'outlined'}
          onClick={() => handleFiltroClick('TODOS')}
          sx={{ cursor: 'pointer' }}
        />
        <Chip
          icon={<WarningIcon />}
          label={`Pendientes: ${estadoCounts.PENDIENTE}`}
          color={filtroActivo === 'PENDIENTE' ? 'info' : 'default'}
          variant={filtroActivo === 'PENDIENTE' ? 'filled' : 'outlined'}
          onClick={() => handleFiltroClick('PENDIENTE')}
          sx={{ cursor: 'pointer' }}
        />
        <Chip
          icon={<CheckCircleIcon />}
          label={`Firmando: ${estadoCounts.FIRMANDO}`}
          color={filtroActivo === 'FIRMANDO' ? 'warning' : 'default'}
          variant={filtroActivo === 'FIRMANDO' ? 'filled' : 'outlined'}
          onClick={() => handleFiltroClick('FIRMANDO')}
          sx={{ cursor: 'pointer' }}
        />
        <Chip
          icon={<CheckCircleIcon />}
          label={`Completados: ${estadoCounts.COMPLETADO}`}
          color={filtroActivo === 'COMPLETADO' ? 'success' : 'default'}
          variant={filtroActivo === 'COMPLETADO' ? 'filled' : 'outlined'}
          onClick={() => handleFiltroClick('COMPLETADO')}
          sx={{ cursor: 'pointer' }}
        />
        <Chip
          icon={<ErrorIcon />}
          label={`Expirados: ${estadoCounts.EXPIRADO}`}
          color={filtroActivo === 'EXPIRADO' ? 'error' : 'default'}
          variant={filtroActivo === 'EXPIRADO' ? 'filled' : 'outlined'}
          onClick={() => handleFiltroClick('EXPIRADO')}
          sx={{ cursor: 'pointer' }}
        />
        <Chip
          icon={<ErrorIcon />}
          label={`Cancelados: ${estadoCounts.CANCELADO}`}
          color={filtroActivo === 'CANCELADO' ? 'error' : 'default'}
          variant={filtroActivo === 'CANCELADO' ? 'filled' : 'outlined'}
          onClick={() => handleFiltroClick('CANCELADO')}
          sx={{ cursor: 'pointer' }}
        />
      </Box>

      {/* Buscador */}
      <Box mb={3}>
        <TextField
          fullWidth
          placeholder="Buscar por título, firmante o estado..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {filteredDocumentos.length === 0 ? (
        <Alert severity="info">
          {searchTerm ? 'No se encontraron documentos que coincidan con la búsqueda.' : 'No hay documentos disponibles.'}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Estado</TableCell>
                <TableCell>Título</TableCell>
                <TableCell>Firmante</TableCell>
                <TableCell>Firmantes</TableCell>
                <TableCell>Creado</TableCell>
                <TableCell>Expira</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDocumentos.map((documento) => (
                <TableRow key={documento.id} hover>
                  <TableCell>
                    <Chip
                      icon={getEstadoIcon(documento.estado)}
                      label={documento.estado}
                      color={getEstadoColor(documento.estado) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 200
                      }}>
                        {documento.titulo}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {documento.firmantes && documento.firmantes.length > 1 ? (
                      <Box>
                        {documento.firmantes.map((firmante, index) => (
                          <Box key={firmante.id} display="flex" alignItems="center" mb={index < documento.firmantes!.length - 1 ? 0.5 : 0}>
                            <Avatar sx={{ width: 20, height: 20, mr: 1 }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                              {firmante.nombre}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="body2">
                          {documento.firmante || 'Sin firmante'}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {documento.totalFirmantes || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <CalendarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                      <Typography variant="body2">
                        {formatDate(documento.fechaCreacion)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <CalendarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                      <Typography variant="body2">
                        {formatDate(documento.fechaExpiracion)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver detalles">
                      <IconButton
                        onClick={() => handleVerDetalle(parseInt(documento.id))}
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal de detalle completo */}
      {selectedDocumentoId && (
        <DocumentoDetalleCompleto
          open={!!selectedDocumentoId}
          onClose={handleCloseDetalle}
          documentoId={selectedDocumentoId}
        />
      )}
    </Container>
  );
};

export default DocumentosLista;
